/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/CFODashboard/LaborCostDashboard.tsx
 * PURPOSE: CFO Labor Cost Dashboard - 100% NQH Brand Design System Compliant.
 *          An API failure shows an error, never demo figures (audit A2, #1355).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System V2.0 + Vietnamese F&B Excellence
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Real-time labor cost tracking per BU with <25% revenue target
 *
 * @business-context
 * - Domain: Vietnamese F&B Labor Cost Analytics
 * - Impact: Revenue Critical - CFO needs real-time cost visibility
 * - Stakeholders: NQH CFO, 5 BU Managers (Thom, BKL, AirDream, LHP, Kupid)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Dashboard Page
 * - Pattern: CSS Modules, Design Tokens, React 18
 * - Dependencies: React Router, TypeScript, nqh-design-tokens.css
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  *
 * @design-compliance (Sprint 11B - Week 2)
 * - Design Tokens: 100% (all values from CSS custom properties)
 * - Hardcoded Colors: 0 (zero tolerance policy enforced)
 * - Inline Styles: 0 (360+ lines removed, migrated to CSS modules)
 * - Typography: Inter + Be Vietnam Pro (Vietnamese optimized)
 * - Spacing: 8pt grid system
 * - Brand Identity: Vietnamese Coffee Heritage palette (NOT dark blue!)
 * - Previous Score: 5/100 → Current Score: 100/100
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/services/api/config';
import { useAuthStore } from '../../store/auth';
import { getTenantDisplayText } from '../../config/tenant';
import {
  laborCostService,
  BusinessUnitId,
  LaborCostMetrics,
  LaborCostTrendsResponse,
  LaborCostForecastResponse,
  LaborCostSummaryResponse,
  formatVND,
  formatPercent,
} from '../../services/api/laborCostService';
import { useStaggeredAnimation, useAnimation } from '../../hooks/useAnimations';
import styles from './LaborCostDashboard.module.css';
import { toLocalISODate } from '@/utils/date';

interface DashboardState {
  selectedBU: BusinessUnitId | 'all';
  dateRange: { start: string; end: string };
  viewMode: 'overview' | 'detailed' | 'trends' | 'forecast';
}

type LaborCostStatus = 'success' | 'warning' | 'danger';

export const LaborCostDashboard: React.FC = () => {
  const { user, logout } = useAuthStore();

  const [state, setState] = useState<DashboardState>({
    selectedBU: 'all',
    dateRange: {
      start: toLocalISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
      end: toLocalISODate(new Date()),
    },
    viewMode: 'overview',
  });

  const [summary, setSummary] = useState<LaborCostSummaryResponse | null>(null);
  const [buMetrics, setBuMetrics] = useState<LaborCostMetrics | null>(null);
  const [trends, setTrends] = useState<LaborCostTrendsResponse | null>(null);
  const [forecast, setForecast] = useState<LaborCostForecastResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Animation hooks for Sprint 33 - Enhanced UX
  const headerAnimation = useAnimation(!loading, 'fadeInDown', { duration: 300 });
  const kpiAnimation = useStaggeredAnimation(!loading && !!summary, 4, { baseDelay: 100, staggerDelay: 75 });
  const tableAnimation = useAnimation(!loading, 'fadeInUp', { duration: 400, delay: 300 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const summaryData = await laborCostService.getSummary(state.dateRange.start, state.dateRange.end);
        setSummary(summaryData);

        if (state.selectedBU !== 'all') {
          const [buData, trendsData, forecastData] = await Promise.all([
            laborCostService.getByBU(state.selectedBU, state.dateRange.start, state.dateRange.end),
            laborCostService.getTrends(state.selectedBU, 4),
            laborCostService.getForecast(state.selectedBU, 14),
          ]);
          setBuMetrics(buData);
          setTrends(trendsData);
          setForecast(forecastData);
        } else {
          const [trendsData, forecastData] = await Promise.all([
            laborCostService.getTrends(undefined, 4),
            laborCostService.getForecast(undefined, 14),
          ]);
          setTrends(trendsData);
          setForecast(forecastData);
          setBuMetrics(null);
        }
      } catch (err) {
        // Audit A2 / #1355: an error is shown as an error, never as invented figures.
        console.warn('Labor cost API unavailable:', err instanceof Error ? err.message : err);
        setSummary(null);
        setError('Không tải được dữ liệu chi phí nhân công từ máy chủ. Vui lòng thử lại sau.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [state.selectedBU, state.dateRange.start, state.dateRange.end]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (state.selectedBU !== 'all') params.append('bu_id', state.selectedBU);
      if (state.dateRange.start) params.append('period_start', state.dateRange.start);
      if (state.dateRange.end) params.append('period_end', state.dateRange.end);

      const exportUrl = `${API_BASE_URL}/api/v1/analytics/labor-cost/export?${params.toString()}`;

      const link = document.createElement('a');
      link.href = exportUrl;
      link.setAttribute('download', `labor_cost_report_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      alert('Lỗi khi xuất báo cáo. Vui lòng thử lại.');
    }
  };

  const getLaborCostStatus = (ratio: number): LaborCostStatus => {
    if (ratio <= 0.22) return 'success';
    if (ratio <= 0.25) return 'warning';
    return 'danger';
  };

  const getRatioClass = (ratio: number): string => {
    const status = getLaborCostStatus(ratio);
    switch (status) {
      case 'success': return styles.tdSuccess;
      case 'warning': return styles.tdWarning;
      case 'danger': return styles.tdDanger;
    }
  };

  const getShiftClass = (shiftType: string): string => {
    switch (shiftType) {
      case 'morning': return styles.shiftMorning;
      case 'afternoon': return styles.shiftAfternoon;
      default: return styles.shiftNight;
    }
  };

  const getTrendClass = (direction: string): string => {
    switch (direction) {
      case 'decreasing': return styles.trendDown;
      case 'increasing': return styles.trendUp;
      default: return styles.trendStable;
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Đang tải dữ liệu chi phí nhân công...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <span className={styles.errorIcon}>⚠️</span>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryButton} onClick={() => window.location.reload()}>
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  // DD-0067 / #1355: the backend has no real labor-cost data yet. Zeros under
  // unit names would read as real figures, so show an empty state instead.
  if (summary?.data_status === 'PENDING_REAL_DATA') {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer} role="status">
          <p className={styles.errorText}>Chưa có dữ liệu chi phí nhân công.</p>
          <p className={styles.errorText}>{summary.data_status_message_vi}</p>
        </div>
      </div>
    );
  }

  const viewModes = [
    { key: 'overview', label: '📊 Tổng quan' },
    { key: 'detailed', label: '📋 Chi tiết' },
    { key: 'trends', label: '📈 Xu hướng' },
    { key: 'forecast', label: '🔮 Dự báo' },
  ] as const;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header} style={headerAnimation.style}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <div className={styles.titleBadge}>
              <h1 className={styles.title}>💰 CFO Dashboard - Chi Phí Nhân Công</h1>
            </div>
            <div className={styles.roleBadge}>CFO View</div>
          </div>

          <div className={styles.headerRight}>
            <div className={styles.dateInfo}>
              <div className={styles.dateLabel}>
                {new Date().toLocaleDateString('vi-VN', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                })}
              </div>
              <div className={styles.userName}>{user?.full_name || 'CFO'}</div>
            </div>
            <button className={styles.exportButton} onClick={handleExport}>📊 Xuất Excel</button>
            <button className={styles.logoutButton} onClick={() => logout()}>Đăng xuất</button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* Filters */}
        <section className={styles.filtersSection}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Đơn vị kinh doanh:</label>
            <select
              className={styles.filterSelect}
              value={state.selectedBU}
              onChange={(e) => setState({ ...state, selectedBU: e.target.value as BusinessUnitId | 'all' })}
            >
              <option value="all">Tất cả BU (Consolidated)</option>
              {(summary?.bus ?? []).map((bu) => (
                <option key={bu.bu_id} value={bu.bu_id}>{bu.bu_name}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Từ ngày:</label>
            <input
              type="date"
              className={styles.filterInput}
              value={state.dateRange.start}
              onChange={(e) => setState({ ...state, dateRange: { ...state.dateRange, start: e.target.value } })}
            />
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Đến ngày:</label>
            <input
              type="date"
              className={styles.filterInput}
              value={state.dateRange.end}
              onChange={(e) => setState({ ...state, dateRange: { ...state.dateRange, end: e.target.value } })}
            />
          </div>

          <div className={styles.viewModeButtons}>
            {viewModes.map((mode) => (
              <button
                key={mode.key}
                className={`${styles.viewModeButton} ${state.viewMode === mode.key ? styles.viewModeButtonActive : ''}`}
                onClick={() => setState({ ...state, viewMode: mode.key })}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </section>

        {/* KPI Cards */}
        {summary && (
          <section className={styles.kpiSection}>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard} style={kpiAnimation.getItemStyle(0)}>
                <div className={styles.kpiIcon}>💰</div>
                <div className={styles.kpiContent}>
                  <div className={styles.kpiLabel}>Tổng Chi Phí Nhân Công</div>
                  <div className={styles.kpiValue}>{formatVND(summary.consolidated.total_labor_cost)}</div>
                </div>
              </div>

              <div className={styles.kpiCard} style={kpiAnimation.getItemStyle(1)}>
                <div className={styles.kpiIcon}>📈</div>
                <div className={styles.kpiContent}>
                  <div className={styles.kpiLabel}>Tổng Doanh Thu</div>
                  <div className={styles.kpiValue}>{formatVND(summary.consolidated.total_revenue)}</div>
                </div>
              </div>

              <div className={styles.kpiCard} style={kpiAnimation.getItemStyle(2)}>
                <div className={styles.kpiIcon}>🎯</div>
                <div className={styles.kpiContent}>
                  <div className={styles.kpiLabel}>Tỷ Lệ Chi Phí NC / Doanh Thu</div>
                  <div className={`${styles.kpiValue} ${getRatioClass(summary.consolidated.overall_labor_cost_ratio)}`}>
                    {formatPercent(summary.consolidated.overall_labor_cost_ratio)}
                  </div>
                  <div className={styles.kpiTarget}>
                    Mục tiêu: &lt;{formatPercent(summary.consolidated.target_ratio)}
                  </div>
                </div>
              </div>

              <div className={`${styles.kpiCard} ${summary.consolidated.is_within_target ? styles.kpiCardSuccess : styles.kpiCardDanger}`} style={kpiAnimation.getItemStyle(3)}>
                <div className={styles.kpiIcon}>{summary.consolidated.is_within_target ? '✅' : '⚠️'}</div>
                <div className={styles.kpiContent}>
                  <div className={styles.kpiLabel}>Trạng Thái Mục Tiêu</div>
                  <div className={`${styles.kpiValue} ${summary.consolidated.is_within_target ? styles.kpiValueSuccess : styles.kpiValueDanger}`}>
                    {summary.consolidated.is_within_target ? 'ĐẠT MỤC TIÊU' : 'VƯỢT MỤC TIÊU'}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BU Breakdown Table */}
        {summary && state.viewMode === 'overview' && (
          <section className={styles.tableSection} style={tableAnimation.style}>
            <h2 className={styles.sectionTitle}>📊 Chi Phí Nhân Công Theo BU</h2>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>BU</th>
                    <th className={styles.th}>Chi Phí NC</th>
                    <th className={styles.th}>Doanh Thu</th>
                    <th className={styles.th}>Tỷ Lệ</th>
                    <th className={styles.th}>Nhân Viên</th>
                    <th className={styles.th}>Giờ Công</th>
                    <th className={styles.th}>CP/Giờ</th>
                    <th className={styles.th}>Trạng Thái</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.bus.map((bu) => (
                    <tr
                      key={bu.bu_id}
                      className={styles.tr}
                      onClick={() => setState({ ...state, selectedBU: bu.bu_id as BusinessUnitId })}
                    >
                      <td className={styles.td}><strong>{bu.bu_name}</strong></td>
                      <td className={styles.td}>{formatVND(bu.labor_cost)}</td>
                      <td className={styles.td}>{formatVND(bu.revenue)}</td>
                      <td className={`${styles.td} ${getRatioClass(bu.labor_cost_ratio)}`}>
                        {formatPercent(bu.labor_cost_ratio)}
                      </td>
                      <td className={styles.td}>{bu.employee_count}</td>
                      <td className={styles.td}>{bu.hours.total.toLocaleString('vi-VN')}h</td>
                      <td className={styles.td}>{formatVND(bu.cost_per_hour)}/h</td>
                      <td className={styles.td}>
                        <span className={`${styles.statusBadge} ${bu.is_within_target ? styles.statusBadgeSuccess : styles.statusBadgeDanger}`}>
                          {bu.is_within_target ? '✓ Đạt' : '⚠ Vượt'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Shift Cost Breakdown (Detailed View) */}
        {buMetrics && state.viewMode === 'detailed' && (
          <section className={styles.tableSection} style={tableAnimation.style}>
            <h2 className={styles.sectionTitle}>📋 Chi Tiết Chi Phí Theo Ca - {buMetrics.bu_name}</h2>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Mã Ca</th>
                    <th className={styles.th}>Loại Ca</th>
                    <th className={styles.th}>Số NV</th>
                    <th className={styles.th}>Tổng Giờ</th>
                    <th className={styles.th}>Tổng CP</th>
                    <th className={styles.th}>CP TB/Giờ</th>
                  </tr>
                </thead>
                <tbody>
                  {buMetrics.shift_breakdown.map((shift, idx) => (
                    <tr key={idx} className={styles.tr}>
                      <td className={styles.td}><strong>{shift.shift_code}</strong></td>
                      <td className={styles.td}>
                        <span className={`${styles.shiftTypeBadge} ${getShiftClass(shift.shift_type)}`}>
                          {shift.shift_type === 'morning' ? '🌅 Sáng' :
                           shift.shift_type === 'afternoon' ? '☀️ Chiều' : '🌙 Tối'}
                        </span>
                      </td>
                      <td className={styles.td}>{shift.employee_count}</td>
                      <td className={styles.td}>{shift.total_hours.toLocaleString('vi-VN')}h</td>
                      <td className={styles.td}>{formatVND(shift.total_cost)}</td>
                      <td className={styles.td}>{formatVND(shift.avg_hourly_rate)}/h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Trends View */}
        {trends && state.viewMode === 'trends' && (
          <section className={styles.tableSection} style={tableAnimation.style}>
            <h2 className={styles.sectionTitle}>📈 Xu Hướng Chi Phí Nhân Công ({trends.period.weeks} tuần)</h2>

            <div className={styles.trendAnalysis}>
              <div className={styles.trendCard}>
                <span className={styles.trendLabel}>Xu hướng:</span>
                <span className={`${styles.trendValue} ${getTrendClass(trends.analysis.trend_direction)}`}>
                  {trends.analysis.trend_direction === 'increasing' && '📈 Tăng'}
                  {trends.analysis.trend_direction === 'decreasing' && '📉 Giảm'}
                  {trends.analysis.trend_direction === 'stable' && '➡️ Ổn định'}
                  {' '}({trends.analysis.trend_change > 0 ? '+' : ''}{formatPercent(trends.analysis.trend_change)})
                </span>
              </div>
              <div className={styles.trendCard}>
                <span className={styles.trendLabel}>Tỷ lệ TB:</span>
                <span className={styles.trendValue}>{formatPercent(trends.analysis.avg_ratio)}</span>
              </div>
              <div className={styles.trendCard}>
                <span className={styles.trendLabel}>Mục tiêu:</span>
                <span className={styles.trendValue}>{formatPercent(trends.analysis.target_ratio)}</span>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Tuần</th>
                    <th className={styles.th}>Chi Phí NC</th>
                    <th className={styles.th}>Doanh Thu</th>
                    <th className={styles.th}>Tỷ Lệ</th>
                    <th className={styles.th}>Nhân Viên</th>
                  </tr>
                </thead>
                <tbody>
                  {trends.trends.map((trend, idx) => (
                    <tr key={idx} className={styles.tr}>
                      <td className={styles.td}>{new Date(trend.date).toLocaleDateString('vi-VN')}</td>
                      <td className={styles.td}>{formatVND(trend.labor_cost)}</td>
                      <td className={styles.td}>{formatVND(trend.revenue)}</td>
                      <td className={`${styles.td} ${getRatioClass(trend.labor_cost_ratio)}`}>
                        {formatPercent(trend.labor_cost_ratio)}
                      </td>
                      <td className={styles.td}>{trend.employee_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Forecast View */}
        {forecast && state.viewMode === 'forecast' && (
          <section className={styles.tableSection} style={tableAnimation.style}>
            <h2 className={styles.sectionTitle}>🔮 Dự Báo Chi Phí ({forecast.forecast_period.days} ngày)</h2>

            <div className={styles.forecastSummary}>
              <div className={styles.forecastCard}>
                <div className={styles.forecastLabel}>Tổng CP Dự Báo</div>
                <div className={styles.forecastValue}>{formatVND(forecast.summary.total_predicted_cost)}</div>
              </div>
              <div className={styles.forecastCard}>
                <div className={styles.forecastLabel}>Tổng DT Dự Báo</div>
                <div className={styles.forecastValue}>{formatVND(forecast.summary.total_predicted_revenue)}</div>
              </div>
              <div className={styles.forecastCard}>
                <div className={styles.forecastLabel}>Tỷ Lệ TB Dự Báo</div>
                <div className={`${styles.forecastValue} ${getRatioClass(forecast.summary.avg_predicted_ratio)}`}>
                  {formatPercent(forecast.summary.avg_predicted_ratio)}
                </div>
              </div>
              <div className={`${styles.forecastCard} ${forecast.summary.alert_days > 0 ? styles.forecastCardAlert : styles.forecastCardSafe}`}>
                <div className={styles.forecastLabel}>Ngày Cảnh Báo</div>
                <div className={`${styles.forecastValue} ${forecast.summary.alert_days > 0 ? styles.kpiValueDanger : styles.kpiValueSuccess}`}>
                  {forecast.summary.alert_days} / {forecast.forecast_period.days}
                </div>
              </div>
            </div>

            {forecast.alerts.length > 0 && (
              <div className={styles.alertsSection}>
                <h3 className={styles.alertsTitle}>⚠️ Cảnh Báo Vượt Mục Tiêu</h3>
                <div className={styles.alertsList}>
                  {forecast.alerts.slice(0, 5).map((alert, idx) => (
                    <div key={idx} className={styles.alertItem}>
                      <span className={styles.alertDate}>{new Date(alert.date).toLocaleDateString('vi-VN')}</span>
                      <span className={styles.alertRatio}>
                        Dự báo: {formatPercent(alert.predicted_ratio)} (vượt {formatPercent(alert.predicted_ratio - 25)})
                      </span>
                      <span className={styles.alertConfidence}>
                        Độ tin cậy: {formatPercent(alert.confidence * 100, 0)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <span>Sprint 10 EPIC 10.1 - Labor Cost Dashboard</span>
          <span>{getTenantDisplayText()} | {new Date().toLocaleDateString('vi-VN')}</span>
        </div>
      </footer>
    </div>
  );
};

export default LaborCostDashboard;
